"use client";
import useTranslate from "@app/_hooks/useTranslate";

import ProductGrid from "@app/_components/ProductGrid/ProductGrid";
import ActiveFilters from "@blocks/CategoryPageComponents/activeFilters";
import Filter from "@blocks/CategoryPageComponents/Filter";
import SortBy from "@blocks/CategoryPageComponents/sortBy";
import Link from "@components/Link";
import { Button, Dialog } from "@components/ngg";
import { Pagination } from "@components/Pagination";
import ProductCard from "@components/ProductCard";
import { Heading, Paragraph } from "@components/ui";
import { useSearch } from "@server/hooks";
import { useSearchParams } from "next/navigation";
import { useEffect, useMemo, useState } from "react";

import {
  PRODUCTS_PER_PAGE,
  TOTAL_PRODUCTS_WITH_EAGER_LOADING,
} from "@/config/products";
import { cn } from "@/lib/utils";
import useSortingOptions from "@/utils/hooks/useSortingOptions";
import getPageNumberTitle from "@/utils/seo/getPageNumberTitle";
import { usePluralizedTranslation } from "@app/_hooks/usePluralizedTranslation";

const CloseButton = (onClose: () => void, label: string) => (
  <button type="button" onClick={onClose} className="uppercase underline">
    {label}
  </button>
);

const ClearAll = (onClick: () => void, label: string) => (
  <button
    type="button"
    onClick={onClick}
    className="mr-3 ml-auto uppercase underline"
  >
    {label}
  </button>
);

type Props = {
  slug: string;
  pageNumber: number;
  initialData: any;
  isEmptyResponse?: boolean;
  locale: string | undefined;
};

export default function Search(props: Props) {
  const t = useTranslate();
  const { slug, pageNumber, isEmptyResponse, locale, initialData } = props;

  const [mobileFilterMenuOpen, setMobileFilterMenuOpen] = useState(false);
  const searchParams = useSearchParams();
  const pluralize = usePluralizedTranslation();

  const query = searchParams?.get("query") as string;

  const { data, updateFilter, sort, updateSort, page } = useSearch({
    query,
    initialData,
    disabled: isEmptyResponse,
    pageIndex: pageNumber - 1,
  });

  const totalHits = data?.items?.length ?? data?.count ?? 0;
  const sortingOptions = useSortingOptions();

  const clearFilter = () => {
    if (!data?.filter) return;

    const filter = [...data.filter];
    const clearedFilter = filter.map((facet) => {
      const item = facet;
      item.values = [];
      return item;
    });
    updateFilter(clearedFilter);
    setMobileFilterMenuOpen(false);
  };

  const selectedFilters = useMemo(() => {
    if (!data?.filter) return 0;
    return data.filter.reduce((acc, facet) => {
      if (facet.type !== "checkbox") return acc;
      if (!facet?.values?.length) return acc;
      return acc + facet.values.length;
    }, 0);
  }, [data?.filter]);

  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  }, [query]);

  return (
    <>
      <Heading
        as="h1"
        size={4}
        className="px-3 py-6 font-normal tracking-tight"
      >
        {!isEmptyResponse ? (
          <>
            {getPageNumberTitle({
              defaultTitle: `${t("searchResultsFor")}: “${query}“`,
              pageNumber: page + 1,
              t,
            })}
          </>
        ) : (
          <>{t("noResultsFor", { query })}</>
        )}
      </Heading>

      <div className="flex scroll-mt-[var(--header-height)] justify-between border-y">
        <div className="flex flex-1">
          <Paragraph
            size="xsmall"
            className="flex h-10 shrink-0 items-center pl-3 uppercase"
          >
            {totalHits}{" "}
            {pluralize(
              totalHits,
              t("product") ?? "product",
              t("products") ?? "products",
            )}
          </Paragraph>
        </div>

        <button
          type="button"
          className="flex h-10 items-center px-3 text-sm tracking-wide uppercase lg:hidden"
          onClick={() => setMobileFilterMenuOpen(true)}
        >
          <span>{t("filter")}</span>
          {selectedFilters > 0 && (
            <span className="ml-1 bg-black px-1 text-white tabular-nums">
              {selectedFilters}
            </span>
          )}
        </button>

        {/* Mobile filter menu */}
        <Dialog
          open={mobileFilterMenuOpen}
          onClose={() => setMobileFilterMenuOpen(false)}
          title={t("filter")}
          dialogTransitionProps={{
            enter: "ease-out duration-150",
            enterFrom: "-translate-x-full",
            enterTo: "translate-x-0",
            leave: "ease-in duration-150",
            leaveFrom: "translate-x-0",
            leaveTo: "-translate-x-full",
          }}
          classNames={{
            backdrop: "bg-black/70",
            panel:
              "h-full flex flex-col rounded-none m-0 py-0 max-w-[326px] sm:max-w-[85%] text-left w-full mr-auto",
            panelHeader:
              "flex justify-between border-b border-gray-150 p-3 text-sm font-normal uppercase",
          }}
          SubtitleComponent={() => ClearAll(clearFilter, t("resetAll"))}
          CloseButton={() =>
            CloseButton(() => setMobileFilterMenuOpen(false), t("close"))
          }
        >
          {data?.filter?.length ? (
            <div className="grow overflow-y-auto">
              <Filter
                // initialFilter={initialData.filter}
                filter={data.filter}
                onChange={updateFilter}
              />
            </div>
          ) : null}
          <div className="border-gray-150 border-t px-3 py-4">
            <Button
              fullWidth
              className="py-3 text-sm font-normal uppercase"
              onClick={() => setMobileFilterMenuOpen(false)}
            >
              {t("showProducts")}
            </Button>
          </div>
        </Dialog>

        <SortBy value={sort} options={sortingOptions} onChange={updateSort} />
      </div>

      <div className="flex w-full scroll-mt-[var(--header-height)] items-start empty:hidden">
        {!isEmptyResponse && (
          <div
            className={cn(
              "hidden w-full max-w-[240px] shrink-0 self-stretch lg:block",
            )}
          >
            {data?.filter?.length ? (
              <Filter filter={data.filter} onChange={updateFilter} />
            ) : null}
            {data?.relatedItems?.length ? (
              <div className="mt-4 px-3 text-base">
                <Paragraph className="font-normal">
                  {t("relatedSearches")}
                </Paragraph>
                <ul className="mt-4 space-y-1">
                  {data.relatedItems.map((item) => (
                    <li key={item.id}>
                      <Link
                        locale={locale}
                        href={item.ProductURL}
                        className="underline hover:no-underline"
                      >
                        {item.title}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
          </div>
        )}

        <div className="w-full pb-[50px]">
          <ActiveFilters data={data} updateFilter={updateFilter} />
          {data?.items?.length ? (
            <ProductGrid>
              {data?.items?.map?.((product, index) => (
                <ProductCard.Root
                  key={`${product.id}-${index}`}
                  product={product}
                  isEager={index < TOTAL_PRODUCTS_WITH_EAGER_LOADING}
                >
                  <ProductCard.Link>
                    <ProductCard.Image>
                      <ProductCard.Badges />
                    </ProductCard.Image>
                    <ProductCard.Content />
                  </ProductCard.Link>
                </ProductCard.Root>
              ))}
            </ProductGrid>
          ) : null}
        </div>
      </div>

      <Pagination
        query={query}
        slug={slug}
        page={page + 1}
        state={{
          sort,
          filters: data?.filter ?? [],
        }}
        totalPages={data?.count ? Math.ceil(data.count / PRODUCTS_PER_PAGE) : 0}
      />
    </>
  );
}
